import { useState } from 'react';
import { TaskPage } from '../../../components/workbook/TaskPage';
import { TextArea } from '../../../components/workbook/forms';
import { Callout } from '../../../components/workbook/Callout';

interface StakeholderWin {
  id: string;
  stakeholder: string;
  jobToBeDone: string;
  biggestPain: string;
  winIn90Days: string;
}

export default function StakeholderWinsPage() {
  const [wins, setWins] = useState<StakeholderWin[]>([
    { id: '1', stakeholder: '', jobToBeDone: '', biggestPain: '', winIn90Days: '' },
  ]);
  const [tensions, setTensions] = useState('');
  const [conflicts, setConflicts] = useState('');

  const updateWin = (id: string, field: keyof StakeholderWin, value: string) => {
    setWins(wins.map(w => w.id === id ? { ...w, [field]: value } : w));
  };

  const addWin = () => {
    setWins([...wins, { id: Date.now().toString(), stakeholder: '', jobToBeDone: '', biggestPain: '', winIn90Days: '' }]);
  };

  return (
    <TaskPage
      phase="engage"
      title="Stakeholder Wins"
      pageNumber={3}
      instruction="Make needs explicit. For each stakeholder, identify their job to be done, biggest pain, and what a win looks like."
      whyItMatters="Understanding what each stakeholder needs helps prevent conflicts and align priorities."
      coachRail={{
        timing: "10 minutes",
        tips: "Use the Jobs • Pains • Gains framework for each stakeholder.",
        debrief: "What tensions are we spotting? Where might priorities conflict?"
      }}
    >
      <div className="space-y-6">
        {wins.map((win, index) => (
          <div key={win.id} className="bg-[var(--surface)] p-6 border border-[var(--rule)]">
            <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-4">
              Stakeholder {index + 1}
            </h3>
            <div className="space-y-4">
              <TextArea
                label="Stakeholder name"
                value={win.stakeholder}
                onChange={(e) => updateWin(win.id, 'stakeholder', e.target.value)}
                placeholder="Who is this stakeholder?"
                rows={1}
              />
              <TextArea
                label="Job to be done"
                value={win.jobToBeDone}
                onChange={(e) => updateWin(win.id, 'jobToBeDone', e.target.value)}
                placeholder="What job are they trying to get done?"
                rows={2}
              />
              <TextArea
                label="Biggest pain / risk"
                value={win.biggestPain}
                onChange={(e) => updateWin(win.id, 'biggestPain', e.target.value)}
                placeholder="What's their biggest pain or risk?"
                rows={2}
              />
              <TextArea
                label="A win in 30-90 days would be"
                value={win.winIn90Days}
                onChange={(e) => updateWin(win.id, 'winIn90Days', e.target.value)}
                placeholder="What would success look like for them?"
                rows={2}
              />
            </div>
          </div>
        ))}
        <button
          onClick={addWin}
          className="w-full py-2 px-4 border-2 border-dashed border-[var(--rule)] text-[var(--text-ter)] hover:border-[var(--rule)] hover:text-[var(--text-sec)] transition-colors"
        >
          + Add another stakeholder
        </button>

        <div className="bg-[var(--slate-dim)] border border-[var(--slate)] p-6">
          <h3 className="text-lg font-semibold text-[var(--text-sec)] mb-4">Debrief</h3>
          <TextArea
            label="What tensions are we spotting?"
            value={tensions}
            onChange={(e) => setTensions(e.target.value)}
            placeholder="What conflicting needs or priorities do you see?"
            rows={3}
          />
          <TextArea
            label="Where might priorities conflict?"
            value={conflicts}
            onChange={(e) => setConflicts(e.target.value)}
            placeholder="What trade-offs might we need to make?"
            rows={3}
            className="mt-4"
          />
        </div>

        <Callout type="tip" phase="engage">
          <p className="text-sm">Micro-scaffolds (short prompts, examples) lighten cognitive load while still eliciting the right info.</p>
        </Callout>
      </div>
    </TaskPage>
  );
}






